import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * In-app trail for Back. The browser will not tell us the previous entry's
 * path, so each page records its own. A fresh document (key "default") starts
 * a new trail, which is also what tests get from a new router.
 */
type Entry = { key: string; path: string }
let trail: Entry[] = []

export function resetPageTrail(): void {
  trail = []
}

export function recordLocation(key: string, path: string): void {
  if (key === 'default') {
    trail = [{ key, path }]
    return
  }
  const idx = trail.findIndex((entry) => entry.key === key)
  if (idx >= 0) {
    trail = [...trail.slice(0, idx), { key, path }]
    return
  }
  trail = [...trail, { key, path }]
}

export function previousLocation(): string | null {
  return trail.length >= 2 ? trail[trail.length - 2].path : null
}

export function useRecordPageLocation(): void {
  const location = useLocation()
  const path = `${location.pathname}${location.search}`
  useLayoutEffect(() => {
    recordLocation(location.key, path)
  }, [location.key, path])
}

export function pathOnly(path: string): string {
  const raw = path.split('?')[0].split('#')[0]
  if (raw.length > 1 && raw.endsWith('/')) return raw.slice(0, -1)
  return raw || '/'
}

/** Previous entry is the declared parent, or a page under it. */
export function isParentOrChild(previous: string, parent: string): boolean {
  const prev = pathOnly(previous)
  const base = pathOnly(parent)
  return prev === base || prev.startsWith(`${base}/`)
}

export function backDecision(opts: {
  locationKey: string
  previous: string | null
  parent: string
}): 'history' | 'parent' {
  if (opts.locationKey === 'default') return 'parent'
  if (opts.previous && isParentOrChild(opts.previous, opts.parent)) return 'history'
  return 'parent'
}

/** The Work drawer was opened from the board, not from a deep link. */
export function drawerOpenedFromBoard(opts: { locationKey: string; previous: string | null }): boolean {
  if (opts.locationKey === 'default') return false
  return opts.previous != null && pathOnly(opts.previous) === '/work'
}

/** A short name for the section a path belongs to, for a history Back label. */
const SECTION_LABEL: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/work': 'Work',
  '/calendar': 'Calendar',
  '/inbox': 'Inbox',
  '/customers': 'Contacts',
  '/settings': 'Settings',
  '/marketing': 'Marketing',
  '/reports': 'Reports',
  '/workflows': 'Workflows',
  '/payments': 'Payments',
  '/notifications': 'Notifications',
}

export function sectionBackLabel(path: string | null, fallback: string): string {
  if (!path) return fallback
  const section = '/' + (pathOnly(path).split('/').filter(Boolean)[0] ?? '')
  return SECTION_LABEL[section] ?? fallback
}

/** Parent names longer than this become an icon-only chevron. Never shorten the word. */
export const BACK_LABEL_MAX = 14

export function backLabelText(label: string): { text: string; iconOnly: boolean; aria: string } {
  return {
    text: label,
    iconOnly: label.length > BACK_LABEL_MAX,
    aria: `Back to ${label}`,
  }
}
